import { useAuth } from "@/context/AuthContext";
import { useMemo } from "react";
import type { SavedItem, SavedResourceType } from "@/types/saved";

export type UseSavedItemsResult = {
    items: SavedItem[];
    loading: boolean;
    error: string | null;
    initialized: boolean;
    refresh: () => Promise<void>;
};

/**
 * Returns the current user's saved items from AuthContext (loaded at login).
 * No per-page API calls — all data comes from the session.
 */
export function useSavedItems(
    resourceType?: SavedResourceType,
): UseSavedItemsResult {
    const { savedItems, initializing, isAuthenticated, refreshUser } =
        useAuth();

    const filteredItems = useMemo(() => {
        if (!resourceType) return savedItems;
        return savedItems.filter((item) => item.resourceType === resourceType);
    }, [savedItems, resourceType]);

    const refresh = useMemo(
        () => async () => {
            await refreshUser();
        },
        [refreshUser],
    );

    return {
        items: filteredItems,
        loading: initializing,
        error: null,
        initialized: !initializing && isAuthenticated,
        refresh,
    };
}
